'use client'

import React from 'react'

type Props = {
  children: React.ReactNode
}

type State = {
  hasError: boolean
  error: Error | null
  isOffline: boolean
}

export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    isOffline: false,
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidMount() {
    this.setState({ isOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false })
    window.addEventListener('error', this.handleGlobalError)
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection)
    window.addEventListener('offline', this.handleOffline)
    window.addEventListener('online', this.handleOnline)
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleGlobalError)
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection)
    window.removeEventListener('offline', this.handleOffline)
    window.removeEventListener('online', this.handleOnline)
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[AppErrorBoundary]', error, info)
  }

  private handleGlobalError = (event: ErrorEvent) => {
    console.error('[global error]', event.message, event.error)
  }

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    console.error('[unhandled rejection]', event.reason)
  }

  private handleOffline = () => {
    this.setState({ isOffline: true })
  }

  private handleOnline = () => {
    this.setState({ isOffline: false })
  }

  render() {
    if (this.state.hasError) {
      return <AppErrorScreen error={this.state.error} />
    }

    return (
      <>
        {this.state.isOffline ? (
          <div className="fixed inset-x-4 top-[max(1rem,env(safe-area-inset-top))] z-[100000] rounded-2xl border border-white/25 bg-[#061b32]/90 px-4 py-3 text-center text-sm font-semibold text-white shadow-2xl backdrop-blur">
            Нет интернета. Проверьте соединение и попробуйте ещё раз.
          </div>
        ) : null}
        {this.props.children}
      </>
    )
  }
}

function AppErrorScreen({ error }: { error: Error | null }) {
  return (
    <main className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-[#061b32] px-6 text-center text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(86,196,255,0.45),transparent_32%),linear-gradient(180deg,#7fc7e6_0%,#0d5d83_36%,#15145a_72%,#061124_100%)]" />
      <section className="relative z-10 w-full max-w-sm rounded-[30px] border border-white/30 bg-white/15 px-6 py-8 shadow-2xl backdrop-blur-xl">
        <h1 className="text-3xl font-black uppercase leading-none">Что-то пошло не так</h1>
        <p className="mt-4 text-base leading-snug text-white/85">
          Попробуйте обновить страницу. Если ошибка повторится — проверьте интернет-соединение.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-7 h-14 w-full rounded-full border border-white/35 bg-white/20 text-lg font-black uppercase text-white shadow-lg backdrop-blur"
        >
          Попробовать ещё раз
        </button>
        {process.env.NODE_ENV === 'development' && error ? (
          <pre className="mt-5 max-h-48 overflow-auto rounded-xl bg-black/50 p-3 text-left text-[11px] leading-snug text-white/80">
            {error.stack ?? error.message}
          </pre>
        ) : null}
      </section>
    </main>
  )
}
